"use client"

import Image from "next/image"
import NonLocalizedLink from "next/link"
import { useTranslations } from "next-intl"
import { FacebookIcon } from "react-share"

import { CONST } from "@/constants"
import { Link } from "@/i18n/routing"
import { cn } from "@/lib/utils"
import InstagramIcon from "@/public/socials/instagram.svg"
import TikTokIcon from "@/public/socials/tiktok.svg"
import XIcon from "@/public/socials/x.svg"
import { ROUTES } from "@/routes"
import { useMerchantProfile } from "@/hooks/useGlobalStore"

export default function SpectraFooter() {
  const t = useTranslations("landing")
  const merchant = useMerchantProfile({ required: false })

  return (
    <footer className="animate-footer-entrance bg-black py-6 text-white opacity-0">
      <div className="container mx-auto max-w-3xl px-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Customer Column */}
          <FooterColumn title={t("footer.sections.customer.title")}>
            <FooterLink href={ROUTES.loginCustomer}>{t("footer.sections.customer.login")}</FooterLink>
            <FooterLink href={ROUTES.signupCustomer}>{t("footer.sections.customer.signup")}</FooterLink>
          </FooterColumn>

          {/* Merchant Column */}
          <FooterColumn title={t("footer.sections.merchant.title")}>
            <FooterLink href={ROUTES.loginMerchant}>{t("footer.sections.merchant.login")}</FooterLink>
            <FooterLink href={ROUTES.signupMerchant}>{t("footer.sections.merchant.signup")}</FooterLink>
          </FooterColumn>

          {/* Legal Column */}
          <FooterColumn title={t("footer.sections.legal.title")}>
            <FooterLink href={ROUTES.privacyPolicy}>{t("footer.sections.legal.privacy")}</FooterLink>
            <FooterLink href={ROUTES.termsOfServiceCustomer}>{t("footer.sections.legal.termsCustomer")}</FooterLink>
            {merchant?.merchantProfile && (
              <FooterLink href={ROUTES.termsOfServiceMerchant}>{t("footer.sections.legal.termsMerchant")}</FooterLink>
            )}
          </FooterColumn>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4">
          {/* Social Column */}
          <FooterColumn title={t("footer.sections.social.title")} flexCol={false}>
            <FooterLink href={CONST.SOCIALS.instagram} external>
              <Image src={InstagramIcon} alt="Instagram" width={32} height={32} />
            </FooterLink>
            <FooterLink href={CONST.SOCIALS.tiktok} external className="rounded-full bg-white">
              <Image src={TikTokIcon} alt="TikTok" width={32} height={32} />
            </FooterLink>
            <FooterLink href={CONST.SOCIALS.facebook} external>
              <FacebookIcon size={32} round />
            </FooterLink>
            <FooterLink href={CONST.SOCIALS.x} external>
              <div className="rounded-full bg-white p-[5px] transition-opacity hover:opacity-80">
                <Image src={XIcon} alt="X" width={24} height={24} className="rounded-full" quality={100} priority />
              </div>
            </FooterLink>
          </FooterColumn>
        </div>

        {/* Copyright Notice */}
        <div className="mt-8 text-center">
          <p className="text-sm">{t("footer.copyright", { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  )
}

type FooterColumnProps = {
  title: string
  children: React.ReactNode
  flexCol?: boolean
}

function FooterColumn({ title, children, flexCol = true }: FooterColumnProps) {
  return (
    <div className={cn("flex flex-col items-center")}>
      <h3 className="mb-3 font-semibold">{title}</h3>
      <div className={cn("flex  items-center gap-2", flexCol && "flex-col")}>{children}</div>
    </div>
  )
}

type FooterLinkProps = {
  href: string
  external?: boolean
  children: React.ReactNode
  className?: string
}

function FooterLink({ href, external, children, className }: FooterLinkProps) {
  const Component = external ? NonLocalizedLink : Link
  return (
    <Component
      href={href}
      className={cn("text-center text-gray-400 hover:text-white", className)}
      target="_blank"
      {...(external && {
        rel: "noopener noreferrer",
      })}
    >
      {children}
    </Component>
  )
}
